import { FormEvent, useState } from "react";
import { BookX, History, LogOut, UserMinus } from "lucide-react";
import { Modal } from "../Modal";
import { apiFieldErrors } from "../../utils/apiError";
import type { StaffProfile } from "../../services/staffService";

export type EndEmploymentValues = {
  employment_status: "resigned" | "retired" | "terminated";
  effective_date: string;
  reason: string;
};

const FORM_ID = "end-employment-form";
const OUTCOMES: [EndEmploymentValues["employment_status"], string, string][] = [
  ["resigned", "Resigned", "Left by their own choice"],
  ["retired", "Retired", "Reached the end of their working life"],
  ["terminated", "Terminated", "Employment ended by the school"],
];

export default function EndEmploymentModal({
  profile,
  isSaving,
  error,
  onSubmit,
  onClose,
}: {
  profile: StaffProfile;
  isSaving: boolean;
  error: unknown;
  onSubmit: (values: EndEmploymentValues) => void;
  onClose: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [outcome, setOutcome] = useState<EndEmploymentValues["employment_status"]>("resigned");
  const [acknowledged, setAcknowledged] = useState(false);
  const { fields: errors, message } = apiFieldErrors(
    error,
    ["employment_status", "effective_date", "reason"],
    "Employment could not be ended. Please try again.",
  );
  const initials = profile.user_name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    onSubmit({
      employment_status: outcome,
      effective_date: String(form.get("effective_date")),
      reason: String(form.get("reason") ?? "").trim(),
    });
  };

  return (
    <Modal
      isOpen
      size="xl"
      title="End employment"
      onClose={() => {
        if (!isSaving) onClose();
      }}
      footer={
        <>
          <button type="button" className="btn btn-outline" disabled={isSaving} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form={FORM_ID}
            disabled={!acknowledged || isSaving}
            className="btn gap-2 bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <UserMinus className="h-4 w-4" />
            {isSaving ? "Ending employment…" : "End employment"}
          </button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="font-refined space-y-5">
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
            {initials || "?"}
          </span>
          <div className="min-w-0 text-sm">
            <p className="truncate font-semibold text-gray-900">{profile.user_name}</p>
            <p className="truncate text-xs text-gray-500">
              {profile.position_title} · {profile.department_name || "School-wide"} ·{" "}
              <span className="font-mono">{profile.employee_id}</span>
            </p>
          </div>
        </div>

        <ul className="grid gap-2 text-sm text-gray-700 sm:grid-cols-3">
          {[
            [LogOut, "Their login is revoked."],
            [BookX, "Their classes and subjects are released, and listed for reassignment."],
            [History, "Employment history and payslips are kept."],
          ].map(([Icon, text]) => {
            const ItemIcon = Icon as typeof LogOut;
            return (
              <li key={text as string} className="flex gap-2 rounded-md bg-amber-50 px-3 py-2 text-amber-900">
                <ItemIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span>{text as string}</span>
              </li>
            );
          })}
        </ul>

        {message && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            {message}
          </p>
        )}

        <fieldset>
          <legend className="text-sm font-medium text-gray-700">Outcome *</legend>
          <div className="mt-1.5 grid gap-2 sm:grid-cols-3">
            {OUTCOMES.map(([value, label, description]) => (
              <label
                key={value}
                className={`cursor-pointer rounded-lg border px-3 py-2.5 transition-colors ${
                  outcome === value
                    ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="employment_status"
                  value={value}
                  checked={outcome === value}
                  onChange={() => setOutcome(value)}
                  className="sr-only"
                />
                <span className="block text-sm font-semibold text-gray-900">{label}</span>
                <span className="block text-xs text-gray-500">{description}</span>
              </label>
            ))}
          </div>
          {errors.employment_status && (
            <p role="alert" className="mt-1 text-xs text-red-700">{errors.employment_status}</p>
          )}
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-[12rem_1fr]">
          <label className="block text-sm font-medium text-gray-700">
            Last working day *
            <input
              required
              type="date"
              name="effective_date"
              defaultValue={today}
              min={profile.hire_date}
              className="input mt-1"
            />
            {errors.effective_date && (
              <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
                {errors.effective_date}
              </span>
            )}
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Reason and handover notes *
            <textarea
              required
              minLength={5}
              name="reason"
              rows={4}
              className="input mt-1 h-auto resize-y"
              placeholder="Why employment ended, what was handed over and to whom."
            />
            {errors.reason ? (
              <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
                {errors.reason}
              </span>
            ) : (
              <span className="mt-1 block text-xs font-normal text-gray-500">
                Kept in their employment history.
              </span>
            )}
          </label>
        </div>

        <label className="flex items-start gap-2 rounded-md border border-gray-200 px-3 py-2.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600"
          />
          I understand {profile.user_name.split(" ")[0]} loses access to the system as soon as
          this is saved.
        </label>
      </form>
    </Modal>
  );
}
